import Model, { Request } from '../model'
import APIObject from '../object'
import Enum from '../enums'

import Reservation from './reservation'
import Customer from './customer'
import ReservationCustomer from './reservationCustomer'

export default class Course extends Model {
  static modelName () {
    return 'course'
  }

  objectID () {
    return this.idCourse
  }

  relations () {
    return {

    }
  }

  listRuns (filters = {}) {
    const params = new URLSearchParams()

    if (filters.status) {
      params.append('status', filters.status)
    }

    if (filters.when) {
      params.append('when', filters.when)
    }

    const queryString = params.toString()
    const url = this.constructor.modelBaseURL() + `/${this.objectID()}/runs` + (queryString ? `?${queryString}` : '')

    return this.constructor.requestList(Request.get(url), CourseRun)
  }

  getRun (idCourseRun) {
    const url = this.constructor.modelBaseURL() + `/${this.objectID()}/runs/${idCourseRun}`

    return this.constructor.requestItem(Request.get(url), CourseRun)
  }

  createRun (data) {
    const url = this.constructor.modelBaseURL() + `/${this.objectID()}/runs`

    return this.constructor.requestItem(Request.post(url, data), CourseRun)
  }

  updateRun (data) {
    const url = this.constructor.modelBaseURL() + `/${this.objectID()}/runs/${data.idCourseRun}`

    return this.constructor.requestItem(Request.put(url, data), CourseRun)
  }

  deleteRun (idCourseRun) {
    const url = this.constructor.modelBaseURL() + `/${this.objectID()}/runs/${idCourseRun}`

    return this.constructor.requestItem(Request.delete(url), CourseRun)
  }

  addPhoto (image, options) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/addPhoto'

    const formData = new FormData()
    formData.append('image', image)

    const request = Request.post(url, formData)
    request.options = options

    return this.constructor.requestItem(request, this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  deletePhoto () {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/deletePhoto'
    return this.constructor.requestItem(Request.get(url), this.constructor).then(this.updateSelf(res => this.updateSelf(res)))
  }

  get label () {
    return this.displayName
  }

  get image () {
    if (this.hasImage) {
      return this.images.square
    }

    return null
  }

  get initials () {
    return ((this.name ? this.name.charAt(0) : '')).toUpperCase()
  }
}

export class CourseRun extends Model {
  static modelName () {
    return 'courseRun'
  }

  objectID () {
    return this.idCourseRun
  }

  relations () {
    return {
      makeupConfig: { type: CourseRunMakeupConfig },
      paymentConfig: { type: CourseRunPaymentConfig },
      partialConfig: { type: CourseRunPartialConfig },
      reservations: { type: CourseRunReservation },
      course: { type: Course },
      status: { type: CourseRunStatus },
    }
  }

  customers () {
    const url = this.constructor.modelBaseURL() + `/${this.objectID()}/customers`

    return this.constructor.requestList(Request.get(url), CustomerCourse)
  }

  static upcoming () {
    const url = this.modelBaseURL() + '/upcoming'

    return this.requestList(Request.get(url), CourseRun)
  }

  static list (page = 1, filters = {}) {
    const params = new URLSearchParams({ page })

    if (filters.status) {
      params.append('status', filters.status)
    }

    if (filters.when) {
      params.append('when', filters.when)
    }

    if (filters.course) {
      params.append('course', filters.course)
    }

    const url = this.modelBaseURL() + '/?' + params.toString()

    return this.requestList(Request.get(url), CourseRun)
  }

  addReservations (reservations) {
    const url = this.constructor.modelBaseURL() + `/${this.objectID()}/addReservations`

    return this.constructor.requestItem(Request.post(url, { reservations }), CourseRun)
  }

  removeReservations (reservations) {
    const url = this.constructor.modelBaseURL() + `/${this.objectID()}/removeReservations`

    return this.constructor.requestItem(Request.post(url, { reservations }), CourseRun)
  }

  availableReservations ({ serviceIDs, dates, employeeIDs }) {
    const url = this.constructor.modelBaseURL() + `/${this.objectID()}/availableReservations`

    const request = {
      serviceIDs,
      dates,
      employeeIDs,
    }

    return this.constructor.requestList(Request.jsonPost(url, request), Reservation)
  }

  cancel ({ cancelReservations, sendEmail } = { cancelReservations: false, sendEmail: false }) {
    const url = this.constructor.modelBaseURL() + `/${this.objectID()}/cancel`

    return this.constructor.requestItem(Request.put(url, {
      cancelReservations,
      sendEmail,
    }), CourseRun)
  }
}

export const CourseRunStatus = new Enum({
  confirmed: { value: 1 },
  cancelled: { value: 2 },
})


export class CustomerCourse extends Model {
  static modelName () {
    return 'customerCourse'
  }

  objectID () {
    return this.idCustomerCourse
  }

  relations () {
    return {
      course: { type: Course },
      courseRun: { type: CourseRun },
      customer: { type: Customer },
      payment: { type: CustomerCoursePayment },
      reservations: { type: CustomerCourseReservation },
      status: { type: CustomerCourseStatus },
    }
  }

  cancel ({ sendEmail } = { sendEmail: false }) {
    const url = this.constructor.modelBaseURL() + `/${this.objectID()}/cancel`

    return this.constructor.requestItem(Request.put(url, {
      sendEmail,
    }), CustomerCourse)
  }

  static enrol ({
    idCourseRun,
    idCustomer,
    price,
    deposit,
    isEarly,
    paymentOption,
    requestChannels,
    sendEmail,
  }) {
    const url = this.modelBaseURL()

    const body = {
      idCourseRun,
      idCustomer,
    }

    if (price !== undefined && price !== null) {
      body.price = price
    }
    if (deposit !== undefined && deposit !== null) {
      body.deposit = deposit
    }
    if (isEarly !== undefined) {
      body.isEarly = isEarly
    }
    if (paymentOption) {
      body.paymentOption = paymentOption
    }
    if (requestChannels?.length > 0 && paymentOption === 'request') {
      body.requestChannels = requestChannels
    }
    if (sendEmail !== undefined) {
      body.sendEmail = sendEmail
    }

    return this.requestData(Request.post(url, body))
  }

  availableMakeups ({ idReservationCustomer }) {
    const url = this.constructor.modelBaseURL() + `/${this.objectID()}/availableMakeups?idReservationCustomer=${idReservationCustomer}`

    return this.constructor.requestData(Request.get(url))
  }

  bookMakeup ({ idReservationCustomer, idReservation }) {
    const url = this.constructor.modelBaseURL() + `/${this.objectID()}/bookMakeup`

    return this.constructor.requestItem(Request.post(url, {
      idReservationCustomer,
      idReservation,
    }), CustomerCourse)
  }
}

export const CustomerCourseStatus = new Enum({
  confirmed: { value: 1 },
  cancelled: { value: 2 },
})

export class CustomerCoursePayment extends APIObject {
  relations () {
    return {
      installments: { type: CustomerCoursePaymentInstallment },
    }
  }
}

export class CustomerCoursePaymentInstallment extends APIObject {
}

export class CustomerCourseReservation extends APIObject {
  relations () {
    return {
      reservation: { type: Reservation },
      reservationCustomer: { type: ReservationCustomer },
      makeupReservationCustomer: { type: ReservationCustomer },
    }
  }
}

export class CourseRunCustomer extends APIObject {
  relations () {
    return {
      customer: { type: Customer },
      customerCourse: { type: CustomerCourse },
    }
  }
}

export class CourseRunReservation extends APIObject {
  relations () {
    return {
      reservation: { type: Reservation },
      courseRun: { type: CourseRun },
    }
  }
}


export class CourseRunMakeupConfig extends APIObject {
  relations () {
    return {
      courseRuns: { type: CourseRun },
      reservations: { type: Reservation },
    }
  }
}

export class CourseRunPaymentConfig extends APIObject {
  relations () {
    return {
      mode: { type: CourseRunPaymentMode },
    }
  }
}

export const CourseRunPaymentMode = new Enum({
  full: { value: 0 },
  instalments: { value: 1 },
})

export const CourseRunPartialMode = new Enum({
  none: { value: 0 },
  late: { value: 1 },
  select: { value: 2 },
})

export class CourseRunPartialConfig extends APIObject {
  relations () {
    return {
      mode: { type: CourseRunPartialMode },
    }
  }
}
